<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useDropdownState } from 'src/composables/useDropdownState';
import SharedDropdown from 'src/components/shared/SharedDropdown.vue';
import 'src/assets/ticket-edit-property.css';

const props = defineProps<{
  expectedDuration?: { hours: number; minutes: number };
}>();

const emit = defineEmits<{
  'update:expectedDuration': [duration: { hours: number; minutes: number }];
}>();

const rootEl = ref<HTMLElement>();
const { isOpen, toggle, close } = useDropdownState(rootEl);

const durationHours = Array.from({ length: 9 }, (_, i) => i);
const minutes = [0, 15, 30, 45];

const expectedHours = ref(props.expectedDuration?.hours ?? 0);
const expectedMinutes = ref(props.expectedDuration?.minutes ?? 0);

watch(() => props.expectedDuration, (d) => {
  if (d) { expectedHours.value = d.hours; expectedMinutes.value = d.minutes; }
}, { immediate: true });

const formattedDuration = computed(() => {
  if (!expectedHours.value && !expectedMinutes.value) return '—';
  const parts = [];
  if (expectedHours.value) parts.push(`${expectedHours.value}h`);
  if (expectedMinutes.value) parts.push(`${expectedMinutes.value}m`);
  return parts.join(' ');
});

function selectDurationHour(h: number): void {
  expectedHours.value = h;
  emit('update:expectedDuration', { hours: h, minutes: expectedMinutes.value });
}

function selectDurationMinute(m: number): void {
  expectedMinutes.value = m;
  emit('update:expectedDuration', { hours: expectedHours.value, minutes: m });
  close();
}

function clearDuration(): void {
  expectedHours.value = 0;
  expectedMinutes.value = 0;
  emit('update:expectedDuration', { hours: 0, minutes: 0 });
  close();
}
</script>

<template>
  <div ref="rootEl" class="relative">
    <div class="prop-row" @click="toggle">
      <span class="prop-label">Duration</span>
      <span class="text-s font-medium text-surface-700 dark:text-surface-300">
        {{ formattedDuration }}
      </span>
    </div>
    <SharedDropdown :open="isOpen" min-width="180px" :show-clear="!!(expectedHours || expectedMinutes)" clear-label="Clear duration" @clear="clearDuration" @click.stop>
      <div class="flex gap-1 p-1.5">
        <div class="flex-1">
          <div class="text-[10px] font-semibold text-surface-400 uppercase text-center mb-1">Hours</div>
          <button
            v-for="h in durationHours"
            :key="h"
            class="te-dropdown-item !justify-center !py-1 text-xs font-mono"
            :class="{ 'te-dropdown-item-active': expectedHours === h }"
            @click.stop="selectDurationHour(h)"
          >
            {{ h }}h
          </button>
        </div>
        <div class="w-px bg-surface-200 dark:bg-surface-700 my-2" />
        <div class="flex-1">
          <div class="text-[10px] font-semibold text-surface-400 uppercase text-center mb-1">Min</div>
          <button
            v-for="m in minutes"
            :key="m"
            class="te-dropdown-item !justify-center !py-1 text-xs font-mono"
            :class="{ 'te-dropdown-item-active': expectedMinutes === m }"
            @click.stop="selectDurationMinute(m)"
          >
            {{ m.toString().padStart(2, '0') }}
          </button>
        </div>
      </div>
    </SharedDropdown>
  </div>
</template>
